import { useState } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { exportTimeEntries, exportEmployees, exportSchedules, exportVacations, exportAnalyticsReport } from '../lib/api';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { toast } from 'sonner';
import { FileSpreadsheet, Download, Clock, Users, Calendar, Palmtree, BarChart3, FileText, Loader2 } from 'lucide-react';

export const ReportsPage = () => {
  const { hasRole } = useAuth();
  const [loading, setLoading] = useState({});
  const [dateRange, setDateRange] = useState({
    start_date: '',
    end_date: ''
  });
  const [vacationStatus, setVacationStatus] = useState('');
  const [exportFormat, setExportFormat] = useState('csv');

  const downloadFile = (blob, filename) => {
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    window.URL.revokeObjectURL(url);
    document.body.removeChild(a);
  };

  const handleExport = async (type) => {
    setLoading(prev => ({ ...prev, [type]: true }));
    try {
      let response;
      let filename;
      const ext = exportFormat === 'pdf' ? 'pdf' : 'csv';
      
      switch (type) {
        case 'time-entries':
          response = await exportTimeEntries({ ...dateRange, format: exportFormat });
          filename = `time_entries_${new Date().toISOString().split('T')[0]}.${ext}`;
          break;
        case 'employees':
          response = await exportEmployees({ format: exportFormat });
          filename = `employees_${new Date().toISOString().split('T')[0]}.${ext}`;
          break;
        case 'schedules':
          response = await exportSchedules({ ...dateRange, format: exportFormat });
          filename = `schedules_${new Date().toISOString().split('T')[0]}.${ext}`;
          break;
        case 'vacations':
          response = await exportVacations({ status_filter: vacationStatus || undefined, format: exportFormat });
          filename = `vacations_${new Date().toISOString().split('T')[0]}.${ext}`;
          break;
        case 'analytics':
          response = await exportAnalyticsReport({ ...dateRange, format: exportFormat });
          filename = `analytics_report_${new Date().toISOString().split('T')[0]}.${ext}`;
          break;
        default:
          throw new Error('Unknown export type');
      }
      
      downloadFile(response.data, filename);
      toast.success(`${type.replace('-', ' ')} exported as ${exportFormat.toUpperCase()}`);
    } catch (error) {
      toast.error(error.response?.data?.detail || `Failed to export ${type}`);
    } finally {
      setLoading(prev => ({ ...prev, [type]: false }));
    }
  };

  const reports = [
    {
      id: 'time-entries',
      title: 'Time Entries',
      description: 'Export all clock in/out records with employee details and hours worked',
      icon: Clock,
      color: 'text-blue-600',
      bgColor: 'bg-blue-50',
      roles: ['admin', 'manager'],
      hasDateFilter: true
    },
    {
      id: 'employees',
      title: 'Employee Directory',
      description: 'Export employee list with roles, departments, and contact information',
      icon: Users,
      color: 'text-emerald-600',
      bgColor: 'bg-emerald-50',
      roles: ['admin', 'manager'],
      hasDateFilter: false
    },
    {
      id: 'schedules',
      title: 'Schedules',
      description: 'Export shift schedules with assignments and branch information',
      icon: Calendar,
      color: 'text-violet-600',
      bgColor: 'bg-violet-50',
      roles: ['admin', 'manager', 'supervisor'],
      hasDateFilter: true
    },
    {
      id: 'vacations',
      title: 'Vacation Requests',
      description: 'Export leave requests with status and approval details',
      icon: Palmtree,
      color: 'text-cyan-600',
      bgColor: 'bg-cyan-50',
      roles: ['admin', 'manager', 'supervisor'],
      hasDateFilter: false,
      hasStatusFilter: true
    },
    {
      id: 'analytics',
      title: 'Analytics Report',
      description: 'Comprehensive report with organization metrics and statistics',
      icon: BarChart3,
      color: 'text-amber-600',
      bgColor: 'bg-amber-50',
      roles: ['admin', 'manager'],
      hasDateFilter: true
    }
  ];

  const filteredReports = reports.filter(report => hasRole(report.roles));

  return (
    <div className="space-y-8" data-testid="reports-page">
      {/* Header */}
      <div>
        <h1 className="text-3xl font-bold tracking-tight font-['Manrope']">
          Reports & Exports
        </h1>
        <p className="text-muted-foreground mt-1">
          Generate and download data exports in CSV or PDF format
        </p>
      </div>

      {/* Filters Card */}
      <Card>
        <CardHeader>
          <CardTitle className="text-base font-['Manrope']">Export Options</CardTitle>
          <CardDescription>
            Configure date range and export format
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 max-w-2xl">
            <div className="space-y-2">
              <Label>Start Date</Label>
              <Input
                type="date"
                value={dateRange.start_date}
                onChange={(e) => setDateRange(prev => ({ ...prev, start_date: e.target.value }))}
                data-testid="report-start-date"
              />
            </div>
            <div className="space-y-2">
              <Label>End Date</Label>
              <Input
                type="date"
                value={dateRange.end_date}
                onChange={(e) => setDateRange(prev => ({ ...prev, end_date: e.target.value }))}
                data-testid="report-end-date"
              />
            </div>
            <div className="space-y-2">
              <Label>Export Format</Label>
              <Select value={exportFormat} onValueChange={setExportFormat}>
                <SelectTrigger data-testid="export-format-select">
                  <SelectValue placeholder="Select format" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="csv">
                    <div className="flex items-center gap-2">
                      <FileSpreadsheet className="w-4 h-4" />
                      CSV (Spreadsheet)
                    </div>
                  </SelectItem>
                  <SelectItem value="pdf">
                    <div className="flex items-center gap-2">
                      <FileText className="w-4 h-4" />
                      PDF (Document)
                    </div>
                  </SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Reports Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {filteredReports.map((report) => (
          <Card key={report.id} className="flex flex-col" data-testid={`report-card-${report.id}`}>
            <CardHeader>
              <div className="flex items-start gap-4">
                <div className={`w-12 h-12 rounded-xl ${report.bgColor} flex items-center justify-center flex-shrink-0`}>
                  <report.icon className={`w-6 h-6 ${report.color}`} />
                </div>
                <div>
                  <CardTitle className="text-lg font-['Manrope']">{report.title}</CardTitle>
                  <CardDescription className="mt-1">
                    {report.description}
                  </CardDescription>
                </div>
              </div>
            </CardHeader>
            <CardContent className="flex-1 flex flex-col justify-end">
              {report.hasStatusFilter && (
                <div className="mb-4">
                  <Label className="text-sm">Filter by Status</Label>
                  <Select value={vacationStatus || 'all'} onValueChange={(v) => setVacationStatus(v === 'all' ? '' : v)}>
                    <SelectTrigger className="mt-1" data-testid="vacation-status-filter">
                      <SelectValue placeholder="All statuses" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">All</SelectItem>
                      <SelectItem value="pending">Pending</SelectItem>
                      <SelectItem value="approved">Approved</SelectItem>
                      <SelectItem value="rejected">Rejected</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              )}
              
              <div className="flex items-center gap-2 text-xs text-muted-foreground mb-3">
                {exportFormat === 'pdf' ? (
                  <FileText className="w-3 h-3" />
                ) : (
                  <FileSpreadsheet className="w-3 h-3" />
                )}
                <span>{exportFormat.toUpperCase()} Format</span>
                {report.hasDateFilter && (
                  <>
                    <span>•</span>
                    <span>Supports date filter</span>
                  </>
                )}
              </div>
              
              <Button
                onClick={() => handleExport(report.id)}
                disabled={loading[report.id]}
                className="w-full"
                data-testid={`export-${report.id}-btn`}
              >
                {loading[report.id] ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Generating {exportFormat.toUpperCase()}...
                  </>
                ) : (
                  <>
                    <Download className="w-4 h-4 mr-2" />
                    Export as {exportFormat.toUpperCase()}
                  </>
                )}
              </Button>
            </CardContent>
          </Card>
        ))}
      </div>
    </div>
  );
};
